import CustomModal from '../../../../../components/Modal/CustomModal';
import React, { useState } from 'react';
import {
    CloseIcon, IconClockTable, IconScheduleTable, InfoChip,
    OutlinedCalendarIcon,
    PeopleTalking
} from '../../../../../components/buttons/IconButtons';
import CustomCard from '../../../../../components/CustomCard';
import CustomUserChip from '../../../../../components/CustomUserChip';
import { POSITION_MODEL } from '../../../../../Helpers/PositionModel';
import CustomDateCalendar from '../../../../../components/CustomDateCalendar';
import NotInformation from '../../../../../components/NotInformation';
import { useTranslation } from 'react-i18next';
import {useSelector} from "react-redux";

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
const BookSpace = (props) => {
    const {
        show, onHide, onConfirm,
        handleChange, allFreeReservation,
        eventDetail,
        handleSelectSlot
    } = props;
    const { t } = useTranslation();
    const [banner, setBanner] = useState(true);
    const { getEventSetting } = useSelector((state) => state?.EmployeeEventsSlice);

    /**
     * Determines if a given date should be disabled based on the event's booking settings.
     *
     * Behavior:
     * - All dates are normalized to midnight to ensure accurate comparisons.
     * - If bookingWindowEnabled is true:
     *    - Dates earlier than (today + minimumBookingNotice) are disabled.
     *    - If advanceBookingLimit > 0, dates after the maximum allowed date (minDate + advance - 1) are also disabled.
     *    - If advanceBookingLimit is 0 or undefined, there is no maximum, so only the minimum is enforced.
     * - If bookingWindowEnabled is false or not set, no dates are disabled (all dates are allowed).
     *
     * Examples:
     * 1. minimumBookingNotice = 5, advanceBookingLimit = 0 → disables next 5 days, all others available.
     * 2. minimumBookingNotice = 5, advanceBookingLimit = 3 → disables next 5 days, only allows 3 days after that.
     *
     * @param {Date} date - The date to check.
     * @returns {boolean} True if the date should be disabled, otherwise false.
     */
   const shouldDisableDate = (date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) return true;

    if (getEventSetting?.bookingWindowEnabled) {
        const minimum = getEventSetting?.minimumBookingNotice || 0;
        const advance = getEventSetting?.advanceBookingLimit ;

            const minDate = new Date(today);
            minDate.setDate(minDate.getDate() + minimum);

            if (typeof advance === 'number' && advance > 0) {
                const maxDate = new Date(minDate);
                maxDate.setDate(minDate.getDate() + advance - 1);
                return selectedDate < minDate || selectedDate > maxDate;
            }

            return selectedDate < minDate;
        }

        return false;
    };

    return (
        <CustomModal
            closeButton={true}
            show={show}
            onHide={() => {
                onHide();
            }}
            onConfirm={() => {
                onConfirm();
            }}
            classNameBody="mt-0 mb-0"
            topLeftHeader={  <div className='d-flex align-items-center justify-content-center gap-2 pt-2'>
                <div className={"d-flex align-items-center"}><OutlinedCalendarIcon className="icon-color-shades-950"/></div>
                <p className="fs-24px ibl-font-w-600 font-color-general-950">{t("book_a_space")}</p>
            </div>}
        >
            <CustomCard classes={"w-100"} classCard={'card-border-8px border-0 p-10px'} classContent={'p-0 m-0'}>
                <CustomCard classCard={'card-border-8px'} classContent={'p-0'}>
                    <div className="">
                        <PeopleTalking className="w-100 h-100" />
                    </div>

                    <div className="grid-container row">
                        <div className="d-flex align-items-center position-relative top-left py-3 col-sm-12 col-md-8">
                            <div className='d-flex align-items-center'>
                                <IconScheduleTable className={"mr-8px mb-1 icon-color-shades-800"} />
                               <p className="fs-14px font-color-general-950 ibl-font-w-700"> {t("select_day_booked")}</p>
                            </div>
                        </div>
                        <div className="d-flex align-items-center position-relative top-right py-3 col-4">
                            <div className='d-flex align-items-center'>
                                <IconClockTable className={"mr-8px mb-1 icon-color-shades-800"} />
                                <p className="fs-14px font-color-general-950 ibl-font-w-700">{t("select_time")}</p>
                            </div>
                        </div>
                        <div className="d-flex justify-content-center align-items-center position-relative bottom-left col-8">
                            <CustomDateCalendar
                                id="dpc_bs_date_event"
                                name='dateSelected'
                                onChange={handleChange}
                                shouldDisableDate={shouldDisableDate}
                            />
                        </div>
                        <div
                            className={`d-flex position-relative bottom-right col-4 p-0 m-0 ${(!eventDetail?.dateSelected || allFreeReservation?.length === 0) && 'p-0 m-0'}`}
                        >
                            {allFreeReservation?.length > 0
                                ?
                                (
                                    <div className='maxh-320px overflow-y-auto px-0'>
                                        <div className='row px-0'>
                                            {banner &&
                                                <div className='col-12 py-1 pl-2 pr-1'>
                                                    <CustomUserChip
                                                        className="m-0 chip-secondary-150-100 w-100 p-0 justify-content-start"
                                                        name={t("pick_at_least_one_slot")}
                                                        Icon={InfoChip}
                                                        showAvatar={true}
                                                        variant="large"
                                                        iconPosition={POSITION_MODEL.LEFT}
                                                        onDelete={() => {
                                                            setBanner(false);
                                                        }}
                                                        classNameIcon="icon-color-secondary-700"
                                                        deleteIcon={<CloseIcon width={12} height={12} fontSize='small' className={`icon-color-secondary-700 h-18px`} />}
                                                    />
                                                </div>
                                            }

                                            {allFreeReservation?.map((item, index) => (
                                                <div className="col-12 pt-1 pl-2 pr-1">
                                                    <CustomCard
                                                        classCard={'card-border-8px'}
                                                        classContent={`${item.selected ? 'background-primary-800' : 'background-shades-50 background-hover-shades-100'} p-0`}
                                                        id={item?.zoneId}
                                                        key={item?.zoneId}
                                                        click={() => {
                                                            handleSelectSlot(index);
                                                        }}
                                                    >
                                                        <p className={`fs-14px ibl-font-w-600 text-center py-1 ${item?.selected ? 'font-color-general-00' : 'font-color-general-950'}`}>{item.fromTime.slice(0, 5)} {'-'} {item.toTime.slice(0, 5)} </p>
                                                    </CustomCard>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                                :
                                (
                                    <NotInformation
                                        text={!eventDetail?.dateSelected ? t("select_a_date") : t("no_available_slots_for_this_day")}
                                        card={true}
                                        className='m-0 p-0 w-100'
                                    />
                                )
                            }
                        </div>
                    </div>

                </CustomCard>
            </CustomCard>
        </CustomModal>
    );

}

export default BookSpace
